/**
 * Serialize a large import payload into a Blob without ever creating one giant
 * string (V8 throws RangeError: Invalid string length above ~512MB–1GB).
 * Result is still a single application/json body for one import job.
 */
async function buildImportJsonBlob({
  data,
  configuration = null,
  importer_type = '',
  original_file_name = '',
}) {
  const blobParts = []
  let parts = []
  const flush = () => {
    if (parts.length === 0) {
      return
    }
    blobParts.push(new Blob(parts))
    parts = []
  }
  const pushRowArray = async (rows) => {
    parts.push('[')
    for (let i = 0; i < rows.length; i++) {
      if (i > 0) {
        parts.push(',')
      }
      parts.push(JSON.stringify(rows[i]))
      if (parts.length >= 500) {
        flush()
        if (i % 2000 === 0) {
          await new Promise((resolve) => setTimeout(resolve, 0))
        }
      }
    }
    parts.push(']')
  }

  parts.push('{"importer_type":')
  parts.push(JSON.stringify(importer_type || ''))
  parts.push(',"original_file_name":')
  parts.push(JSON.stringify(original_file_name || ''))

  if (configuration) {
    parts.push(',"configuration":{')
    let firstConfigKey = true
    for (const [key, value] of Object.entries(configuration)) {
      if (!firstConfigKey) {
        parts.push(',')
      }
      firstConfigKey = false
      parts.push(JSON.stringify(key))
      parts.push(':')
      if (
        (key === 'upsert_values' || key === 'data') &&
        Array.isArray(value) &&
        value.length > 1000
      ) {
        await pushRowArray(value)
      } else {
        parts.push(JSON.stringify(value))
      }
    }
    parts.push('}')
  }

  parts.push(',"data":')
  await pushRowArray(data || [])
  parts.push('}')
  flush()
  return new Blob(blobParts, { type: 'application/json' })
}

export default (client) => {
  return {
    fetchAll(databaseId) {
      return client.get(`/database/tables/database/${databaseId}/`)
    },
    create(
      databaseId,
      values,
      initialData = null,
      firstRowHeader = false,
      config = null
    ) {
      if (initialData !== null) {
        values.data = initialData
        values.first_row_header = firstRowHeader
      }

      return client.post(
        `/database/tables/database/${databaseId}/async/`,
        values,
        config
      )
    },
    createSync(databaseId, values, initialData = null, firstRowHeader = false) {
      if (initialData !== null) {
        values.data = initialData
        values.first_row_header = firstRowHeader
      }

      return client.post(`/database/tables/database/${databaseId}/`, values)
    },
    async importData(
      tableId,
      data,
      config = null,
      importConfiguration = null,
      metadata = {}
    ) {
      // Build one application/json body as a Blob of small string parts.
      // A single JSON.stringify of the whole payload throws
      // "RangeError: Invalid string length" for DOT-scale imports in V8.
      // This is still one JSON POST (not multipart, not row-chunked jobs).
      const body = await buildImportJsonBlob({
        data,
        configuration: importConfiguration,
        ...metadata,
      })

      return client.post(`/database/tables/${tableId}/import/async/`, body, {
        ...(config || {}),
        headers: {
          ...((config && config.headers) || {}),
          'Content-Type': 'application/json',
        },
        transformRequest: [(d) => d],
        // Long uploads must not be paused/retried mid-body by auth refresh.
        skipAuthRefresh: true,
        maxBodyLength: Infinity,
        maxContentLength: Infinity,
      })
    },
    get(tableId) {
      return client.get(`/database/tables/${tableId}/`)
    },
    update(tableId, values) {
      return client.patch(`/database/tables/${tableId}/`, values)
    },
    order(databaseId, order) {
      return client.post(`/database/tables/database/${databaseId}/order/`, {
        table_ids: order,
      })
    },
    asyncDuplicate(tableId) {
      return client.post(`/database/tables/${tableId}/duplicate/async/`)
    },
    delete(tableId) {
      return client.delete(`/database/tables/${tableId}/`)
    },
  }
}
